import {StrictRouteContext} from "./RouteContext";
import {
    Button, DrawerBody, DrawerHeader, DrawerHeaderTitle,
    OverlayDrawer, Tree, TreeItem, TreeItemLayout,
    makeStyles, shorthands,
    webDarkTheme, webLightTheme,
} from "@fluentui/react-components";
import {
    DarkThemeFilled, DismissFilled, HomeFilled, TextAlignJustify24Filled,
} from "@fluentui/react-icons";
import {Route, Routes, useNavigate} from "react-router-dom";
import HomePage from "./HomePage";
import {useCallback} from "react";
import {Column, Row} from "./common/Layout";
import {useBoolean} from "../../type/States";
import ShoppingRouter from "./shopping/ShoppingRouter";
import UserRouter from "./user/UserRouter";
import CampaignRouter from "./campaign/CampaignRouter";
import {AdminSessionIO} from "../../io/admin/AdminSessionIO";
import PlaceRouter from "./place/PlaceRouter";

const useStyles = makeStyles({
    headerDrawerButton: { ...shorthands.margin("16px") },
    page: { ...shorthands.margin("16px") },
});

export default function Navigation(props: StrictRouteContext) {
    const styles = useStyles();
    const drawerState = useBoolean(false);
    const navigate = useNavigate();

    const onThemeShiftClick = useCallback(() => {
        props.themeState.setValue(
            props.themeState.value === webLightTheme ? webDarkTheme : webLightTheme,
        );
    }, [props.themeState]);

    const deleteSession = async () => {
        try {
            await AdminSessionIO.deleteCurrent();
            window.alert("로그아웃 되었습니다.");
            window.location.reload();
        } catch {
            window.alert("로그아웃 중 오류가 발생했습니다.");
        }
    };

    const DrawerHeaderActions = () => (
        <Row>
            <Button as="a" appearance="subtle" icon={<HomeFilled />} href="/" />
            <Button appearance="subtle" icon={<DarkThemeFilled />} onClick={onThemeShiftClick} />
            <Button appearance="subtle" icon={<DismissFilled />} onClick={drawerState.setFalse} />
        </Row>
    );

    return (
        <>
            <OverlayDrawer
                open={drawerState.value}
                onOpenChange={(_, data) => drawerState.setValue(data.open)}
            >
                <DrawerHeader>
                    <DrawerHeaderTitle action={<DrawerHeaderActions />} />
                </DrawerHeader>

                <DrawerBody>
                    <Tree
                        aria-label="admin navigation"
                        defaultOpenItems={["content"]}
                    >
                        <TreeItem itemType="branch" value="content">
                            <TreeItemLayout>콘텐츠</TreeItemLayout>
                            <Tree>
                                <TreeItem itemType="branch" value="shoppingGroup">
                                    <TreeItemLayout>쇼핑</TreeItemLayout>
                                    <Tree>
                                        <TreeItem itemType="leaf" value="stat" onClick={() => navigate("/admin/shoppings/funnels")}>
                                            <TreeItemLayout>통계</TreeItemLayout>
                                        </TreeItem>
                                        <TreeItem itemType="leaf" value="shopping" onClick={() => navigate("/admin/shoppings")}>
                                            <TreeItemLayout>쇼핑</TreeItemLayout>
                                        </TreeItem>
                                        <TreeItem itemType="leaf" value="mainKeywords" onClick={() => navigate("/admin/shoppings/mainKeywords")}>
                                            <TreeItemLayout>메인키워드</TreeItemLayout>
                                        </TreeItem>
                                        <TreeItem itemType="leaf" value="paths" onClick={() => navigate("/admin/shoppings/paths")}>
                                            <TreeItemLayout>쇼핑 경로</TreeItemLayout>
                                        </TreeItem>
                                        <TreeItem itemType="leaf" value="pathJoints" onClick={() => navigate("/admin/shoppings/pathJoints")}>
                                            <TreeItemLayout>쇼핑 경로 Joint</TreeItemLayout>
                                        </TreeItem>
                                        <TreeItem itemType="leaf" value="pathBrowsers" onClick={() => navigate("/admin/shoppings/pathBrowsers")}>
                                            <TreeItemLayout>쇼핑 경로 Browser</TreeItemLayout>
                                        </TreeItem>
                                        <TreeItem itemType="leaf" value="participations" onClick={() => navigate("/admin/shoppings/participations")}>
                                            <TreeItemLayout>쇼핑 참여</TreeItemLayout>
                                        </TreeItem>
                                        <TreeItem itemType="leaf" value="events" onClick={() => navigate("/admin/shoppings/pathJointEvents")}>
                                            <TreeItemLayout>쇼핑 이벤트</TreeItemLayout>
                                        </TreeItem>
                                        <TreeItem itemType="leaf" value="reports" onClick={() => navigate("/admin/shoppings/pathJointReports")}>
                                            <TreeItemLayout>쇼핑 문의 관리</TreeItemLayout>
                                        </TreeItem>
                                        <TreeItem itemType="leaf" value="reportReasons" onClick={() => navigate("/admin/shoppings/pathJointReportReasons")}>
                                            <TreeItemLayout>쇼핑 문의 항목 관리</TreeItemLayout>
                                        </TreeItem>
                                        <TreeItem itemType="leaf" value="policies" onClick={() => navigate("/admin/shoppings/policies")}>
                                            <TreeItemLayout>쇼핑 참여 정책</TreeItemLayout>
                                        </TreeItem>
                                        <TreeItem itemType="leaf" value="files" onClick={() => navigate("/admin/shoppings/pathJointFiles")}>
                                            <TreeItemLayout>쇼핑 경로 파일</TreeItemLayout>
                                        </TreeItem>
                                        <TreeItem itemType="leaf" value="files" onClick={() => navigate("/admin/shoppings/excelExport")}>
                                            <TreeItemLayout>쇼핑 비동기 엑셀 다운로드</TreeItemLayout>
                                        </TreeItem>
                                    </Tree>
                                </TreeItem>

                                <TreeItem itemType="branch" value="placeGroup">
                                    <TreeItemLayout>플레이스</TreeItemLayout>
                                    <Tree>
                                        <TreeItem itemType="leaf" value="places" onClick={() => navigate("/admin/places")}>
                                            <TreeItemLayout>플레이스 목록</TreeItemLayout>
                                        </TreeItem>
                                        <TreeItem itemType="leaf" value="placeKeywords" onClick={() => navigate("/admin/places/keywords")}>
                                            <TreeItemLayout>키워드</TreeItemLayout>
                                        </TreeItem>
                                        <TreeItem itemType="leaf" value="placeParticipation" onClick={() => navigate("/admin/places/participations")}>
                                            <TreeItemLayout>참여</TreeItemLayout>
                                        </TreeItem>
                                        <TreeItem itemType="leaf" value="placePolicies" onClick={() => navigate("/admin/places/participations/policies")}>
                                            <TreeItemLayout>참여 정책</TreeItemLayout>
                                        </TreeItem>
                                        <TreeItem itemType="leaf" value="placePaths" onClick={() => navigate("/admin/places/paths")}>
                                            <TreeItemLayout>경로</TreeItemLayout>
                                        </TreeItem>
                                        <TreeItem itemType="leaf" value="placePathFiles" onClick={() => navigate("/admin/places/paths/files")}>
                                            <TreeItemLayout>경로 파일</TreeItemLayout>
                                        </TreeItem>
                                        <TreeItem itemType="leaf" value="placePathBrowsers" onClick={() => navigate("/admin/places/paths/browsers")}>
                                            <TreeItemLayout>경로 Browser</TreeItemLayout>
                                        </TreeItem>
                                        <TreeItem itemType="leaf" value="placePathJoints" onClick={() => navigate("/admin/places/paths/joints")}>
                                            <TreeItemLayout>경로 Joint</TreeItemLayout>
                                        </TreeItem>
                                        <TreeItem itemType="leaf" value="placePathJointEvents" onClick={() => navigate("/admin/places/paths/joints/events")}>
                                            <TreeItemLayout>Joint 이벤트</TreeItemLayout>
                                        </TreeItem>
                                        <TreeItem itemType="leaf" value="placePathJointReports" onClick={() => navigate("/admin/places/paths/joints/reports")}>
                                            <TreeItemLayout>Joint 문의 관리</TreeItemLayout>
                                        </TreeItem>
                                        <TreeItem itemType="leaf" value="placePathJointReportReasons" onClick={() => navigate("/admin/places/paths/joints/reports/reasons")}>
                                            <TreeItemLayout>Joint 문의 항목 관리</TreeItemLayout>
                                        </TreeItem>
                                        <TreeItem itemType="leaf" value="placeFunnels" onClick={() => navigate("/admin/places/funnels/stats")}>
                                            <TreeItemLayout>통계</TreeItemLayout>
                                        </TreeItem>
                                    </Tree>
                                </TreeItem>
                            </Tree>
                        </TreeItem>

                        <TreeItem itemType="branch" value="user">
                            <TreeItemLayout>사용자</TreeItemLayout>
                            <Tree>
                                <TreeItem itemType="leaf" value="users" onClick={() => navigate("/admin/users")}>
                                    <TreeItemLayout>사용자</TreeItemLayout>
                                </TreeItem>
                            </Tree>
                        </TreeItem>

                        <TreeItem itemType="branch" value="campaign">
                            <TreeItemLayout>캠페인</TreeItemLayout>
                            <Tree>
                                <TreeItem itemType="leaf" value="campaigns" onClick={() => navigate("/admin/campaigns")}>
                                    <TreeItemLayout>캠페인</TreeItemLayout>
                                </TreeItem>
                                <TreeItem itemType="leaf" value="campaignPolicies" onClick={() => navigate("/admin/campaigns/policies")}>
                                    <TreeItemLayout>캠페인 정책</TreeItemLayout>
                                </TreeItem>
                            </Tree>
                        </TreeItem>
                    </Tree>

                    <Column marginTop="24px">
                        <Button appearance="primary" onClick={deleteSession}>
                            로그아웃
                        </Button>
                    </Column>
                </DrawerBody>
            </OverlayDrawer>

            <Column>
                <Button
                    className={styles.headerDrawerButton}
                    appearance="subtle"
                    icon={<TextAlignJustify24Filled />}
                    onClick={drawerState.setTrue}
                />
                <div className={styles.page}>
                    <Routes>
                        <Route path="/" element={<HomePage {...props} />} />
                        <Route path="/shoppings/*" element={<ShoppingRouter {...props} />} />
                        <Route path="/places/*" element={<PlaceRouter {...props} />} />
                        <Route path="/users/*" element={<UserRouter {...props} />} />
                        <Route path="/campaigns/*" element={<CampaignRouter {...props} />} />
                    </Routes>
                </div>
            </Column>
        </>
    );
}
